export type SanitizeRequestFalsyOptions = {
  /**
   * Keep numeric `0` (default: true). Set false to coerce `0` and `NaN` to `null`.
   */
  preserveZero?: boolean;
  /**
   * Keep boolean `false` (default: true). Set false to coerce `false` to `null`.
   */
  preserveFalse?: boolean;
};

/**
 * GraphQL address: **full** (every field filled) or **empty** (`null`).
 * “Empty” includes default-country-only and partial fills.
 */
const ADDRESS_SHAPE_KEYS = [
  "country",
  "province",
  "district",
  "sector",
  "cell",
  "village",
  "streetAddress"
] as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function isAddressLikeObject(obj: Record<string, unknown>): boolean {
  if (!("country" in obj)) return false;
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;
  return keys.every((k) =>
    (ADDRESS_SHAPE_KEYS as readonly string[]).includes(k)
  );
}

/** Full address: every field below is present and non-empty (after trim for strings). */
function isAddressFull(obj: Record<string, unknown>): boolean {
  return ADDRESS_SHAPE_KEYS.every((k) => {
    const v = obj[k];
    if (v === undefined || v === null) return false;
    if (typeof v === "string") return v.trim() !== "";
    return true;
  });
}

function shouldCoerceLeafToNull(
  value: unknown,
  opts: Required<SanitizeRequestFalsyOptions>
): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (!opts.preserveZero && typeof value === "number") {
    return Number.isNaN(value) || value === 0;
  }
  if (!opts.preserveFalse && typeof value === "boolean" && value === false) {
    return true;
  }
  if (!opts.preserveZero && typeof value === "bigint" && value === 0n) {
    return true;
  }
  return false;
}

/**
 * Deep walk: empty strings / undefined → `null` for GraphQL variables.
 *
 * **`address`** (property name) or address-shaped objects: **`null`** unless every address
 * field is non-empty (default country only → `null`).
 */
export function sanitizeRequestFalsyToNull<T>(
  input: T,
  options?: SanitizeRequestFalsyOptions
): T {
  const opts: Required<SanitizeRequestFalsyOptions> = {
    preserveZero: options?.preserveZero ?? true,
    preserveFalse: options?.preserveFalse ?? true
  };

  const walk = (value: unknown, propertyKey?: string): unknown => {
    if (shouldCoerceLeafToNull(value, opts)) return null;
    if (Array.isArray(value)) return value.map((item) => walk(item));
    if (isPlainObject(value)) {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) {
        out[k] = walk(v, k);
      }
      const treatAsAddress =
        propertyKey === "address" || isAddressLikeObject(out);
      if (treatAsAddress && !isAddressFull(out)) {
        return null;
      }
      return out;
    }
    return value;
  };

  return walk(input) as T;
}
